/*
 * Copyright (c) Nexign, JSC, 2021-2025.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Alexander Golshtein
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.15.0
 */

import { Fragment, useEffect, useMemo, useRef } from 'react';

import { connect, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';


import { ImgStyled } from '@components/common/Block/Img';
import { renderDropFilterButton } from '@components/common/PageTitle/PageTitle';
import { useTable } from '@components/Table/hooks/useTable';
import { ITableField } from '@components/Table/reducers/tableReducer';
import { MapTableNameToApiPath, MapTableNameToListApiPath } from '@components/Table/types/tableMapTypes';
import { TableNameType } from '@components/Table/types/tableTypes';
import {
    API_PAYMENTS,
    API_PAYMENTS_ACCOUNT_ID,
    API_PAYMENTS_ACCOUNT_ID_EXPORT,
    API_PAYMENTS_EXPORT,
} from '@modules/app/Api';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import {
    makeSelectPagePaymentsTranslation,
    makeSelectTranslationCommon,
} from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';
import { makeSelectIsDensity } from '@modules/storage/storageSelectors';
import { IRootReducer } from '@reducers/rootReducer';
import { IRouteParams } from '@routes/constants/routesConstants';
import { stringFormatParams } from '@utils/stringFormat';

import { icons } from '../../../assets';
import { PageTitle, Table } from '../../../components/common';
import Title from '../../../components/common/Title/Title';
import TableColumnUtil from '../../../components/Table/TableColumnUtil';
import DensityButton from '../../../modules/density/components/DensityButton';
import ExportButton from '../../../modules/exports/components/ExportButton';
import PageLocale from '../../../modules/internationalization/components/PageLocale';
import { ICommonTranslation, IPagePaymentsTranslation } from '../../../modules/internationalization/types';
import {
    EPagePaymentsTableField,
    getPagePaymentsTableColumnCellType,
    getPagePaymentsTableColumnFilterType,
    PagePaymentsTableCellConfig,
    PagePaymentsTableColumnNames,
} from '../const/PagePaymentsTable';

const PagePayments = ({
                          isDensity,
                          translationCommon,
                          translationPagePayments,
                      }: IConnectedProps) => {

    const routeParams = useParams<IRouteParams>();

    const dispatch = useDispatch();

    const {
        componentsFeature,
        count,
        data,
        fields,
        isFilterSelected,
        isLoading,
        isTableResizeEnabled,
        sort,
        dropFilterAndSort,
        loadTableList,
        loadTableMeta,
        saveResizedChange,
        setTableSort,
    } = useTable(TableNameType.payments);

    const isEventSend = useRef(false);
    const isFirstLoading = useRef(true);

    const { instructionsLinks: { paymentsGeneral, paymentsExport } } = translationCommon;

    const tableColumnUtil = useMemo(() =>
        new TableColumnUtil<EPagePaymentsTableField, IPagePaymentsTranslation & ICommonTranslation>({
            cellConfig: PagePaymentsTableCellConfig,
            columnNames: PagePaymentsTableColumnNames,
            componentsFeature,
            fields: fields as Array<ITableField<EPagePaymentsTableField>>,
            getColumnCellType: getPagePaymentsTableColumnCellType,
            getColumnFilterType: getPagePaymentsTableColumnFilterType,
            getColumnFixedType: () => false,
            isTableResizeEnabled,
            translation: { ...translationCommon, ...translationPagePayments },
        }), [componentsFeature, fields, isTableResizeEnabled, translationCommon, translationPagePayments]);

    useEffect(() => {
        if (isLoading) {
            isFirstLoading.current = false;
            return;
        }
        if (isFirstLoading.current || isEventSend.current) return;
        dispatch(logBusinessEvent({
            eventName: ELogBusinessEventName.B2B_SHOW_PAYMENTS,
            type: ELogBusinessEventType.common,
        }))
        isEventSend.current = true;
    }, [dispatch, isLoading])

    useEffect(() => {
        MapTableNameToApiPath[TableNameType.payments] = routeParams.clientId
            ? stringFormatParams(API_PAYMENTS_ACCOUNT_ID, { clientId: routeParams.clientId })
            : API_PAYMENTS;
        MapTableNameToListApiPath[TableNameType.payments] = routeParams.clientId
            ? stringFormatParams(API_PAYMENTS_ACCOUNT_ID, { clientId: routeParams.clientId })
            : API_PAYMENTS;

        loadTableMeta();
    }, [loadTableMeta, routeParams.clientId]);

    return (
        <Fragment>
            <PageLocale lazy fileNames={[TranslationFileNamesEnum.pagePayments]}/>

            <Title pageName={ECommonTranslationPages.pagePayments}/>

            <PageTitle
                count={count}
                instructionLink={paymentsGeneral}
                title={translationPagePayments.title.label}
            >
                {
                    // render drop filter button
                    isFilterSelected() && renderDropFilterButton({
                        className: 'page-title__button',
                        onClick: () => dropFilterAndSort({}),
                        value: translationPagePayments.title.dropFilters,
                    })
                }
                <div className="page-title__button" style={{ margin: '0 4px 0 8px' }}>
                    <ImgStyled
                        className="medium pointer"
                        data-name="payments-sync-btn"
                        src={icons.syncBtnIcon}
                        onClick={() => loadTableList({ force: true })}
                    />
                </div>
                <div className="page-title__button">
                    <ExportButton
                        instructionLink={paymentsExport}
                        src={routeParams.clientId
                            ? stringFormatParams(API_PAYMENTS_ACCOUNT_ID_EXPORT, { clientId: routeParams.clientId })
                            : API_PAYMENTS_EXPORT}
                        tableName={TableNameType.payments}
                    />
                </div>
                <div className="page-title__button">
                    <DensityButton page={ECommonTranslationPages.pagePayments}/>
                </div>
            </PageTitle>
            <Table
                isTableFooter
                columns={tableColumnUtil.customizeTableColumns()}
                counter={count}
                data={data}
                disableSortCheckbox={true}
                fields={fields}
                isDensity={isDensity}
                isLoading={isLoading}
                loadTableList={loadTableList}
                saveResizedChange={saveResizedChange}
                setTableSort={setTableSort}
                showCheckedColumn={false}
                sort={sort}
                tableDataName={TableNameType.payments}
                tableName={TableNameType.payments}
            />
        </Fragment>
    );
};

export interface IConnectedProps {
    isDensity: boolean;
    translationCommon: ICommonTranslation;
    translationPagePayments: IPagePaymentsTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isDensity: makeSelectIsDensity(),
    translationCommon: makeSelectTranslationCommon(),
    translationPagePayments: makeSelectPagePaymentsTranslation(),
});

export default connect(mapStateToProps)(PagePayments);
